import type { Client } from '@wf-coe/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Wire-format `Client` — the API serializes Date fields as ISO strings.
 * The domain {@link Client} from `@wf-coe/types` uses real `Date` objects;
 * the SPA pages display the strings directly so we keep them as-is rather
 * than converting on every fetch.
 */
export type ClientDto = Omit<Client, 'createdAt' | 'updatedAt'> & {
  createdAt: string;
  updatedAt: string;
};

/**
 * TanStack Query hook for `GET /v1/clients`. Disabled until the user is
 * signed in (`useApiClient` returns `undefined` pre-auth).
 *
 * Casbin gates `(client, read)` on the server — Admin / Operator / Viewer
 * all match (per slice 14's policy refinement). Unknown roles get 403,
 * which surfaces here as an `ApiError`.
 */
export function useClientsQuery(): UseQueryResult<ClientDto[]> {
  const api = useApiClient();
  return useQuery({
    queryKey: ['clients'],
    queryFn: () => {
      if (!api) {
        throw new Error('useClientsQuery: api client unavailable (no signed-in account)');
      }
      return api.get<ClientDto[]>('/v1/clients');
    },
    enabled: api !== undefined,
  });
}
